import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Avatar,
  Box,
  CircularProgress,
  Rating,
  TextField,
  Typography,
} from "@mui/material";

import { getFileLink } from "../../../util/s3";
import { toastify, toastType } from "../../../util/toastify";
import trade_favicon_transparent_bg from "../../../assets/images/trade_favicon_transparent_bg.png";

// If the reviewer's reply never comes back (socket hiccup), let them try again
// rather than leaving the button spinning forever.
const SUBMIT_TIMEOUT_MS = 10000;

// The task payload carries the reviewer as `review.from`; the live
// "ServiceReviewed" push carries them as `sender`. Normalise to `from` and
// swap the raw avatar key for a signed URL.
const withReviewer = async (review) => {
  if (!review) return null;
  const from = review.from || review.sender;
  if (!from) return review;

  let media = from.media;
  if (media && media.url) {
    try {
      media = { ...media, url: await getFileLink(media.url) };
    } catch (err) {
      console.error(err);
    }
  }
  return { ...review, from: { ...from, media } };
};

// Rating + review panel for a completed task. Rendered at the top of the task
// page (not under the chat) so it's the first thing the receiver sees when
// they open a completed service.
const TDReviewBox = ({
  auth,
  socket,
  roomId,
  taskId,
  providerId,
  oponent,
  taskStatus,
  defaultReview,
}) => {
  const [review, setReview] = useState(null);
  const [star, setStar] = useState(5);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let alive = true;
    withReviewer(defaultReview).then((value) => alive && setReview(value));
    return () => {
      alive = false;
    };
  }, [defaultReview]);

  useEffect(() => {
    if (!socket) return undefined;
    const onReviewed = async (data) => setReview(await withReviewer(data));
    socket.on("TDChatReviewResponse", onReviewed);
    socket.on("ServiceReviewed", onReviewed);
    return () => {
      socket.off("TDChatReviewResponse", onReviewed);
      socket.off("ServiceReviewed", onReviewed);
    };
  }, [socket]);

  // The server flips the task to REVIEWED once the review lands; until then
  // keep the button locked so a double click can't file two reviews.
  useEffect(() => {
    if (taskStatus === "REVIEWED") setSending(false);
  }, [taskStatus]);

  useEffect(() => {
    if (!sending) return undefined;
    const timer = setTimeout(() => setSending(false), SUBMIT_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [sending]);

  const submitReview = () => {
    if (sending) return;
    if (!star) {
      toastify("Please select a star rating.", toastType.warning);
      return;
    }
    if (text.trim() === "") {
      toastify("Please write a few words about the service.", toastType.warning);
      return;
    }
    setSending(true);
    socket.emit("SERVICEREVIEWED", {
      roomId,
      taskId,
      star,
      senderId: auth.user.userId,
      receiverId: oponent,
      text: text.trim(),
    });
    setText("");
  };

  if (taskStatus === "COMPLETEACCEPTED") {
    if (auth.user.userId === providerId) {
      return (
        <Box className="p-6 mt-8 bg-[#f9f9f9] rounded-[20px]">
          <Typography variant="h6">Waiting for review.</Typography>
          <Typography variant="body2" className="text-gray-600">
            The service is complete. Your points will show in your balance and
            the receiver can now leave you a review.
          </Typography>
        </Box>
      );
    }

    return (
      <Box className="p-6 mt-8 bg-[#f9f9f9] rounded-[20px]">
        <Typography variant="h6" className="mb-1">
          Leave a Review
        </Typography>
        <Typography variant="body2" className="text-gray-600 mb-4">
          How was the service? Your rating helps other members.
        </Typography>
        <Box className="flex mb-4 items-center">
          <Rating
            size="large"
            name="task-review-rating"
            value={star}
            onChange={(_event, value) => setStar(value)}
          />
          <Box ml={2}>{(star || 0).toFixed(1)} out of 5.0</Box>
        </Box>
        <Box className="relative flex items-center mb-4 !border-0 bg-white rounded-[20px]">
          <TextField
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submitReview();
              }
            }}
            id="task-review-text"
            label="Type your thoughts here..."
            fullWidth
            multiline
            className="outline-none !border-0"
            minRows={3}
            maxRows={5}
            disabled={sending}
          />
        </Box>
        <Box className="flex justify-end">
          <button
            type="button"
            onClick={submitReview}
            disabled={sending}
            className="flex items-center justify-center border-0 h-12 px-6 text-white rounded-full focus:outline-none transition-colors duration-300 hover:bg-[#35f016] bg-[#22ac0a] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {sending ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <>Submit Review</>
            )}
          </button>
        </Box>
      </Box>
    );
  }

  if (taskStatus === "REVIEWED" && review) {
    const from = review.from;
    return (
      <Box className="p-6 mt-8 bg-[#f9f9f9] rounded-[20px]">
        <Typography variant="h6" className="mb-2">
          Receiver left a {review.star || 0} star review
        </Typography>
        <Box className="flex mb-4 items-center">
          <Rating
            size="large"
            name="task-review-readonly"
            value={review.star || 0}
            readOnly
          />
          <Box ml={2}>{(review.star || 0).toFixed(1)} out of 5.0</Box>
        </Box>
        {from && (
          <Box className="flex items-start p-4 bg-white rounded-[20px]">
            <Avatar
              alt="Review sender avatar"
              src={from.media ? from.media.url : trade_favicon_transparent_bg || ""}
              className="mr-3"
            />
            <Box className="flex flex-col">
              <Typography variant="subtitle1" className="font-semibold">
                {from.firstName || ""} {from.lastName || ""}
              </Typography>
              <Typography variant="body2">{review.desc}</Typography>
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  return null;
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, {})(TDReviewBox);
